import {DOCUMENT} from '@angular/common';
import {Component, ElementRef, Inject, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {Title} from '@angular/platform-browser';

import {ActivatedRoute} from '@angular/router';
import {CoreMenuService} from '@core/components/core-menu/core-menu.service';
import {CoreSidebarService} from '@core/components/core-sidebar/core-sidebar.service';
import {CoreConfigService} from '@core/services/config.service';
import {CoreLoadingScreenService} from '@core/services/loading-screen.service';
import {CoreTranslationService} from '@core/services/translation.service';
import {CoreConfig, CoreMenu} from '@core/types';
import {Store} from '@ngrx/store';
import {locale as menuGerman} from 'app/menu/i18n/de';
import {locale as menuEnglish} from 'app/menu/i18n/en';
import {locale as menuFrench} from 'app/menu/i18n/fr';
import {locale as menuPortuguese} from 'app/menu/i18n/pt';
import {menu} from 'app/menu/menu';

import * as Waves from 'node-waves';
import {of, Subject, Subscription} from 'rxjs';
import {delay, take, takeUntil} from 'rxjs/operators';
import {AddonPermissionService} from './addon-permission/addon-permission.service';
import {AuthenticationService} from './auth/service';
import {GlobalService} from './global/global.service';
import {detectCrmParams} from './global/integrations/integration.helper';
import * as AuthActions from './store/actions/auth.action';
import { HubspotService } from './global/hubspot/hubspot.service';
import { StorageService } from './global/storage.service';


@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
    // @HostListener('window:resize', ['$event'])
    coreConfig: CoreConfig;
    menu: CoreMenu[];
    defaultLanguage: 'en'; // This language will be used as a fallback when a translation isn't found in the current language
    appLanguage: 'en'; // Set application default language i.e fr
    // Private
    private _unsubscribeAll: Subject<void>;
    public domSubscription: Subscription;
    signingArea = false;
    /**
     * Constructor
     *
     * @param {DOCUMENT} document
     * @param {Title} _title
     * @param {Renderer2} _renderer
     * @param {ElementRef} _elementRef
     * @param {CoreConfigService} _coreConfigService
     * @param {CoreSidebarService} _coreSidebarService
     * @param {CoreLoadingScreenService} _coreLoadingScreenService
     * @param {CoreMenuService} _coreMenuService
     * @param {CoreTranslationService} _coreTranslationService
     * @param globalService
     * @param {AuthenticationService} _authenticationService
     * @param store
     * @param _addonPermissionService$
     * @param _activatedRoute$
     */
    constructor(
        @Inject(DOCUMENT) private document: Document,
        private _title: Title,
        private _renderer: Renderer2,
        private _elementRef: ElementRef,
        public _coreConfigService: CoreConfigService,
        private _coreSidebarService: CoreSidebarService,
        private _coreMenuService: CoreMenuService,
        private _coreTranslationService: CoreTranslationService,
        private _coreLoadingScreenService: CoreLoadingScreenService,
        private globalService: GlobalService,
        private _authenticationService: AuthenticationService,
        private store: Store,
        private _addonPermissionService$: AddonPermissionService,
        private _activatedRoute$: ActivatedRoute,
        private hubspotService: HubspotService,
        private _storageService: StorageService
    ) {
        // Get the application main menu
        this.menu = menu;

        // Register the menu to the menu service
        this._coreMenuService.register('main', this.menu);

        // Set the main menu as our current menu
        this._coreMenuService.setCurrentMenu('main');
        //
        // // Add languages to the translation service
        // this._translateService.addLangs(['en', 'fr', 'de', 'pt']);
        //
        // // This language will be used as a fallback when a translation isn't found in the current language
        // this._translateService.setDefaultLang('en');

        // Set the translations for the menu
        this._coreTranslationService.translate(menuEnglish, menuFrench, menuGerman, menuPortuguese);

        // Set the private defaults
        this._unsubscribeAll = new Subject<void>();
    }

    // Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    async ngOnInit() {

        // Clear localStorage config to force new menu collapsed state
        this._storageService.removeItem('config');

        // Force menu to be collapsed
        this._coreConfigService.setConfig({ 
            layout: { 
                menu: { 
                    collapsed: true 
                } 
            } 
        }, { emitEvent: true });

        // Force body classes for collapsed menu
        setTimeout(() => {
            document.body.classList.remove('menu-expanded');
            document.body.classList.add('menu-collapsed');
        }, 200);

        // Init wave effect (Ripple effect)
        Waves.init();


        // Init loadingscreen service
        this._coreLoadingScreenService._init();

        // Subscribe to config changes
        this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
            this.coreConfig = config;


            if (config.layout.menu.collapsed) {
                document.body.classList.remove('menu-expanded');
                document.body.classList.add('menu-collapsed');
            }

            // Set application default language.

            // Change application language? Read the ngxTranslate Fix

            // ? Use app-config.ts file to set default language
            // this._translateService.use(appLanguage);

            // ? OR
            // ? User the current browser lang if available, if undefined use 'en'
            // const browserLang = this._translateService.getBrowserLang();
            // this._translateService.use(browserLang.match(/en|fr|de|pt/) ? browserLang : 'en');

            /**
             * ! Fix : ngxTranslate
             * ----------------------------------------------------------------------------------------------------
             */

            /**
             *
             * Using different language than the default ('en') one i.e French?
             * In this case, you may find the issue where application is not properly translated when your app is initialized.
             *
             * It's due to ngxTranslate module and below is a fix for that.
             * Eventually we will move to the multi language implementation over to the Angular's core language service.
             *
             **/

            // Set the default language to 'en' and then back to 'fr'.

            // setTimeout(() => {
            //   this._translateService.setDefaultLang('fr');
            //   this._translateService.setDefaultLang(appLanguage);
            // });

            /**
             * !Fix: ngxTranslate
             * ----------------------------------------------------------------------------------------------------
             */

            // Layout
            //--------


            // Remove default classes first
            this._elementRef.nativeElement.classList.remove(
                'vertical-layout',
                'vertical-menu-modern',
                'horizontal-layout',
                'horizontal-menu'
            );
            // Add class based on config options
            if (this.coreConfig.layout.type === 'vertical') {
                this._elementRef.nativeElement.classList.add('vertical-layout', 'vertical-menu-modern');
            } else if (this.coreConfig.layout.type === 'horizontal') {
                this._elementRef.nativeElement.classList.add('horizontal-layout', 'horizontal-menu');
            }

            // Navbar
            //--------

            // Remove default classes first
            this._elementRef.nativeElement.classList.remove(
                'navbar-floating',
                'navbar-static',
                'navbar-sticky',
                'navbar-hidden'
            );

            // Add class based on config options
            if (this.coreConfig.layout.navbar.type === 'navbar-static-top') {
                this._elementRef.nativeElement.classList.add('navbar-static');
            } else if (this.coreConfig.layout.navbar.type === 'fixed-top') {
                this._elementRef.nativeElement.classList.add('navbar-sticky');
            } else if (this.coreConfig.layout.navbar.type === 'floating-nav') {
                this._elementRef.nativeElement.classList.add('navbar-floating');
            } else {
                this._elementRef.nativeElement.classList.add('navbar-hidden');
            }

            // Footer
            //--------

            // Remove default classes first
            this._elementRef.nativeElement.classList.remove('footer-fixed', 'footer-static', 'footer-hidden');

            // Add class based on config options
            if (this.coreConfig.layout.footer.type === 'footer-sticky') {
                this._elementRef.nativeElement.classList.add('footer-fixed');
            } else if (this.coreConfig.layout.footer.type === 'footer-static') {
                this._elementRef.nativeElement.classList.add('footer-static');
            } else {
                this._elementRef.nativeElement.classList.add('footer-hidden');
            }

            // Blank layout
            if (
                this.coreConfig.layout.menu.hidden &&
                this.coreConfig.layout.navbar.hidden &&
                this.coreConfig.layout.footer.hidden
            ) {
                this._elementRef.nativeElement.classList.add('blank-page');
                // ! Fix: Transition issue while coming from blank page
                this.domSubscription = of(null).
                pipe(
                    delay(0),
                    take(1)
                ).subscribe(() => {
                    this._renderer.setAttribute(
                        this._elementRef.nativeElement.getElementsByClassName('app-content')[0],
                        'style',
                        'transition:none'
                    );
                });
            } else {
                this._elementRef.nativeElement.classList.remove('blank-page');
                // ! Fix: Transition issue while coming from blank page

                this.domSubscription = of(null).
                pipe(
                    delay(0),
                    take(1)
                ).subscribe(() => {
                    this._renderer.setAttribute(
                        this._elementRef.nativeElement.getElementsByClassName('app-content')[0],
                        'style',
                        'transition:300ms ease all'
                    );
                })
                // If navbar hidden
                if (this.coreConfig.layout.navbar.hidden) {
                    this._elementRef.nativeElement.classList.add('navbar-hidden');
                }
                // Menu (Vertical menu hidden)
                if (this.coreConfig.layout.menu.hidden) {
                    this._renderer.setAttribute(this._elementRef.nativeElement, 'data-col', '1-column');
                } else {
                    this._renderer.removeAttribute(this._elementRef.nativeElement, 'data-col');
                }
                // Footer
                if (this.coreConfig.layout.footer.hidden) {
                    this._elementRef.nativeElement.classList.add('footer-hidden');
                }
            }

            // Skin Class (Adding to body as it requires highest priority)
            if (this.coreConfig.layout.skin !== undefined) {
                this.document.body.classList.remove('default-layout', 'bordered-layout', 'dark-layout', 'semi-dark-layout');
                this.document.body.classList.add(this.coreConfig.layout.skin + '-layout');
            }
        });

        // Set the application page title
        this._title.setTitle(this.coreConfig.app.appTitle);
        const paths = ['/s/', '/sender/', '/shareable/', '/download/', '/online/', 'export-pdf-view'];
        if (paths.some(path => window.location.hash.indexOf(path) > -1)) {
            this.signingArea = true;
            this._coreConfigService.setConfig({
                layout: {
                    navbar: {
                        hidden: true
                    },
                    footer: {
                        hidden: true
                    },
                    menu: {
                        hidden: true
                    },
                    customizer: false,
                    enableLocalStorage: false
                }
            });
        } else {
            this.signingArea = false;
        }
        this._activatedRoute$.queryParams.subscribe((route) => {
            const crmResult = {};
            const isCrmUser = detectCrmParams(route);
            if(isCrmUser){
                Object.keys(route).map(key => crmResult[key] = route[key]);
                this.store.dispatch(AuthActions.loadCrmData(route));
            }
            if (!paths.some(path => window.location.hash.indexOf(path) > -1)) {
                this.hubspotService.loadTrackingScript();
            }
        });
        this.globalService.storeSubscribe();
        if (await this._authenticationService.isLoggedIn() && (!this.signingArea || window.location.hash.indexOf('/sender/') > -1)) {
            this._addonPermissionService$.detectAddonPermission();
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
        if(this.domSubscription){
            this.domSubscription.unsubscribe();
        }
    }

    // Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebar(key): void {
        this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
    }

    // Funtion for Left Menubar Show/Hide on resolution
    onResize(event) {
        const windowWidth = event.target.innerWidth;
        const element: HTMLElement = document.querySelector('.toggle-icon.collapse-toggle-icon') as HTMLElement;
        if (element) {
            if (windowWidth > 991 && windowWidth < 1500) {
                this._coreSidebarService.getSidebarRegistry('menu').close();
                if (document.querySelector('core-sidebar').classList.contains('expanded')) {
                    element?.click();
                }
            } else {
                if (windowWidth > 991) {
                    this._coreSidebarService.getSidebarRegistry('menu').open()
                    if (!document.querySelector('core-sidebar').classList.contains('expanded')) {
                        element?.click();
                    }
                }
            }
        }
        if (this.signingArea) {
            const templatePageContainer = document.querySelector('.template-main-container');
            if(templatePageContainer){
                if (window.outerWidth <= 911) {
                    const totalWidth = window.innerWidth;
                    const contentWidth = document.documentElement.clientWidth;
                    const scrollbarWidth = totalWidth - contentWidth;
                    const adjustedWidth = totalWidth - scrollbarWidth;
                    const width = adjustedWidth;
                    const templateWidth = 810;
                    this._renderer.setStyle(templatePageContainer, 'transform', `translate3d(0px, 0px, 0px) scale3d(${(width / templateWidth)}, ${(width / templateWidth)}, 1)`);
                    const templatePageHeight = document.querySelector('.template-page').getBoundingClientRect().height;
                    this._renderer.setStyle(templatePageContainer, 'height', `${templatePageHeight}px`);
                } else {
                    this._renderer.setStyle(templatePageContainer, 'height', ``);
                    this._renderer.setStyle(templatePageContainer, 'transform', ``);
                }
            }
        }
    }


}
